import { useEffect, useState } from "react";
import { signFetch } from "../api/client";
import { clockLine, hashGroups } from "../lib/format";
import { auditLabel, cleanDetail } from "../lib/trace";
import type { SignAct, SignIntegrity, SignPiece } from "../types";
import { IcoClose, IcoCopy, IcoShield } from "../ui/icons";

function rankLabel(i: number): string {
  return i === 0 ? "1re partie" : `${i + 1}e partie`;
}

function groupPieces(pieces: SignPiece[]): { label: string; items: SignPiece[] }[] {
  const map = new Map<number, { name: string; items: SignPiece[] }>();
  const orphans: SignPiece[] = [];
  for (const row of pieces) {
    const pid = row.party_id ?? 0;
    if (!pid) {
      orphans.push(row);
      continue;
    }
    const g = map.get(pid) ?? { name: row.party_name ?? "", items: [] };
    g.items.push(row);
    if (row.party_name) g.name = row.party_name;
    map.set(pid, g);
  }
  const groups = [...map.values()].map((g, i) => ({
    label: `${rankLabel(i)}${g.name ? ` · ${g.name}` : ""}`,
    items: g.items,
  }));
  if (orphans.length) groups.push({ label: "Autres", items: orphans });
  return groups;
}

function HashCard({ label, hint, value }: { label: string; hint: string; value: string }) {
  const [ok, setOk] = useState(false);
  if (!value) return null;
  return (
    <div className="sg-trace__hash">
      <div className="sg-trace__hash-top">
        <div>
          <p>{label}</p>
          <small>{hint}</small>
        </div>
        <button
          type="button"
          className="sg-btn ghost"
          onClick={() => {
            void navigator.clipboard.writeText(value).then(() => {
              setOk(true);
              window.setTimeout(() => setOk(false), 1400);
            });
          }}
        >
          <IcoCopy />
          {ok ? "Copié" : "Copier"}
        </button>
      </div>
      <code>{hashGroups(value)}</code>
    </div>
  );
}

export function TraceDrawer({ id, onClose }: { id: number; onClose: () => void }) {
  const [data, setData] = useState<SignIntegrity | null>(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setData(null);
    signFetch<{ integrity: SignIntegrity }>(`documents.php?id=${id}&action=integrity`)
      .then((r) => setData(r.integrity))
      .catch((e) => setErr(e instanceof Error ? e.message : "Preuve indisponible."));
  }, [id]);

  const verdict = !data
    ? null
    : !data.content_hash
      ? { kind: "draft", title: "Pas encore d’empreinte", text: "Aucune empreinte SHA-256 n’est encore enregistrée pour ce document." }
      : !data.frozen
        ? data.match
          ? { kind: "hold", title: "Empreinte d’import intacte", text: "Le fichier correspond à l’empreinte enregistrée à la réception. Elle sera figée à l’envoi." }
          : { kind: "bad", title: "Fichier altéré", text: "Le document ne correspond plus à l’empreinte enregistrée à l’import. L’envoi doit être refusé." }
        : data.match
          ? { kind: "ok", title: "Empreinte intacte", text: "L’empreinte recalculée est identique à celle enregistrée à l’envoi. L’intégrité du document est confirmée." }
          : { kind: "bad", title: "Empreinte altérée", text: "L’empreinte actuelle diffère de celle enregistrée. Le document a pu être modifié en dehors de l’application." };

  const acts: SignAct[] = data?.signers?.length
    ? data.signers
    : data?.signature
      ? [data.signature]
      : [];

  return (
    <div className="sg-trace" role="dialog" aria-modal="true" aria-label="Preuve d’intégrité">
      <button type="button" className="sg-trace__back" aria-label="Fermer" onClick={onClose} />
      <aside className="sg-trace__sheet">
        <header className="sg-trace__head">
          <div>
            <p>Preuve d’intégrité</p>
            {data ? <span>{data.title}</span> : null}
          </div>
          <button type="button" className="sg-btn ghost" onClick={onClose}>
            <IcoClose />
            Fermer
          </button>
        </header>
        {err ? <p className="sg-err">{err}</p> : null}
        {!data && !err ? <p className="sg-legal">Lecture des empreintes et du journal…</p> : null}
        {data && verdict ? (
          <>
            <div className={`sg-trace__verdict is-${verdict.kind}`}>
              <IcoShield />
              <div>
                <strong>{verdict.title}</strong>
                <p>{verdict.text}</p>
              </div>
              <em>{data.algo}</em>
            </div>
            <p className="sg-legal">Contrôle {clockLine(data.checked_at, data.timezone || "UTC")}</p>
            <dl className="sg-trace__meta">
              <div>
                <dt>État</dt>
                <dd>{data.frozen ? "Figé" : "Brouillon"}</dd>
              </div>
              <div>
                <dt>Envoi</dt>
                <dd>{clockLine(data.sent_at)}</dd>
              </div>
              <div>
                <dt>1re lecture</dt>
                <dd>{clockLine(data.viewed_at)}</dd>
              </div>
              <div>
                <dt>Signature</dt>
                <dd>{clockLine(data.signed_at)}</dd>
              </div>
            </dl>
            {data.content_hash || data.computed_hash ? (
              <section>
                <h3>Empreinte du PDF</h3>
                <p className="sg-legal">Empreintes SHA-256. L’original est figé à l’envoi. Le PDF final livré est le contrat scellé après signature. Toute modification produit une empreinte différente.</p>
                <HashCard label="PDF original" hint={data.frozen ? "Figée à l’envoi" : "Prise à l’import"} value={data.content_hash} />
                <HashCard label="Recalculée maintenant" hint="Doit être identique à l’original" value={data.computed_hash} />
                {data.sealed_hash ? <HashCard label="PDF final livré" hint="Contrat scellé après signature" value={data.sealed_hash} /> : null}
                {data.pack_hash ? <HashCard label="Pack assemblé" hint="Contrat + rapport de preuve" value={data.pack_hash} /> : null}
              </section>
            ) : null}
            {acts.length > 0 ? (
              <section>
                <h3>{acts.length > 1 ? "Actes de signature" : "Acte de signature"}</h3>
                <div className="sg-trace__acts">
                  {acts.map((act, i) => (
                    <div key={act.proof_hash || `${act.signer_name}-${i}`} className="sg-trace__act">
                      {acts.length > 1 ? (
                        <p className="sg-trace__rank">
                          {rankLabel(i)}
                          {act.party_name && act.party_name !== act.signer_name ? ` · ${act.party_name}` : ""}
                        </p>
                      ) : null}
                      <p className="sg-trace__who">{act.signer_name}</p>
                      <p className="sg-legal">{clockLine(act.signed_at, "UTC")}</p>
                      {act.phrase ? <blockquote>{act.phrase}</blockquote> : null}
                      {act.ip ? <p className="sg-legal">IP {act.ip}</p> : null}
                      {act.user_agent ? <p className="sg-trace__ua">{act.user_agent}</p> : null}
                      <HashCard label="Empreinte de l’acte" hint="Signataire, date, mention et adresse IP, liés à l’empreinte du document" value={act.proof_hash} />
                    </div>
                  ))}
                </div>
                <p className="sg-trace__legal">
                  Signature électronique au sens de l’article 1367 du Code civil et du règlement eIDAS. Horodatée, liée
                  à l’empreinte SHA-256 du document, et conservée au journal.
                </p>
              </section>
            ) : null}
            {data.pieces && data.pieces.length > 0 ? (
              <section>
                <h3>Pièces du dossier</h3>
                {groupPieces(data.pieces).map((g) => (
                  <div key={g.label} className="sg-trace__act">
                    <p className="sg-trace__rank">{g.label}</p>
                    {g.items.map((p) => (
                      <div key={p.id}>
                        <p className="sg-trace__who">{p.ask_label || p.filename}</p>
                        {p.filename && p.ask_label ? <p className="sg-legal">{p.filename}</p> : null}
                        <HashCard label="Empreinte de la pièce" hint="Figée au dépôt" value={p.content_hash} />
                      </div>
                    ))}
                  </div>
                ))}
              </section>
            ) : null}
            <section>
              <h3>Journal</h3>
              {data.audit_chain && data.audit_chain.status !== "empty" ? (
                <div
                  className={`sg-trace__verdict is-${data.audit_chain.status === "ok" ? "ok" : data.audit_chain.status === "broken" ? "bad" : "hold"}`}
                  style={{ marginBottom: "0.85rem" }}
                >
                  <IcoShield />
                  <div>
                    <strong>
                      {data.audit_chain.status === "ok"
                        ? "Chaîne intacte"
                        : data.audit_chain.status === "broken"
                          ? "Chaîne rompue"
                          : "Chaîne partielle"}
                    </strong>
                    <p>
                      {data.audit_chain.status === "ok"
                        ? "Chaque acte est lié au précédent (HMAC-SHA256). Une ligne retirée ou modifiée rompt la chaîne."
                        : data.audit_chain.status === "broken"
                          ? "Le journal ne correspond plus à la chaîne enregistrée."
                          : "Des lignes n’ont pas d’empreinte (journal antérieur à la chaîne)."}
                    </p>
                  </div>
                  <em>{data.audit_chain.algo}</em>
                </div>
              ) : null}
              {data.audit_chain?.head ? (
                <HashCard
                  label="Tête de chaîne"
                  hint={`${data.audit_chain.entries} événement${data.audit_chain.entries > 1 ? "s" : ""}`}
                  value={data.audit_chain.head}
                />
              ) : null}
              {data.audit.length === 0 ? (
                <p className="sg-legal">Aucun événement encore.</p>
              ) : (
                <ol className="sg-timeline">
                  {data.audit.map((row, i) => (
                    <li key={`${row.created_at}-${i}`}>
                      <strong>{auditLabel(row.action)}</strong>
                      <br />
                      <span className="sg-legal">
                        {clockLine(row.created_at, row.clock_tz || "UTC")}
                        {row.actor_name ? ` · ${row.actor_kind === "signer" ? "Signataire" : row.actor_name}` : ""}
                        {cleanDetail(row.detail) ? ` · ${cleanDetail(row.detail)}` : ""}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          </>
        ) : null}
      </aside>
    </div>
  );
}
